import React, { createContext } from 'react';

import {
    DropAnimation,
    UniqueIdentifier,
    defaultDropAnimationSideEffects,
} from '@dnd-kit/core';

import {
    AnimateLayoutChanges,
    defaultAnimateLayoutChanges
} from '@dnd-kit/sortable';

import { Identifiable } from '../DragAndDrop/types';
import { CardVariant } from '../../models/CardVariant';

export interface ContainerProps {
    children: React.ReactNode;
    columns?: number;
    label?: string;
    style?: React.CSSProperties;
    horizontal?: boolean;
    hover?: boolean;
    scrollable?: boolean;
    shadow?: boolean;
    placeholder?: boolean;
    unstyled?: boolean;
    onClick?(): void;
    onRemove?(): void;
    classNames?: {
        default?: string,
        hover?: string,
        list?: string
    }
}

export type DropTabProps = {
    id: TabName,
    text: string,
    isActive: boolean,
    hideMenu?: boolean
}

export interface PlayDeckProps {
    variantQuantity: Record<string, number>;
    variantsByIdDictionary: Record<string, CardVariant>;
}

export type PlayableVariant = Identifiable & {
    value: CardVariant & {
        isDull: boolean,
        isFrozen: boolean,
        counter: number
    }
}

export type PlayState = {
    deck: PlayableVariant[],
    breakZone: PlayableVariant[],
    damageZone: PlayableVariant[],
    hand: PlayableVariant[],
    limitBreak: PlayableVariant[],
    field1: PlayableVariant[],
    field2: PlayableVariant[],
    field3: PlayableVariant[],
    removedFromGame: PlayableVariant[],
    revealed: PlayableVariant[]
}

export function getDefaultPlayState(): PlayState {
    return {
        breakZone: [],
        damageZone: [],
        deck: [],
        field1: [],
        field2: [],
        field3: [],
        hand: [],
        limitBreak: [],
        removedFromGame: [],
        revealed: []
    }
}

export interface SortableItemProps {
    containerId: keyof PlayState;
    item: PlayableVariant;
    index: number;
    disabled?: boolean;
    hidden?: boolean
}

export const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
        styles: {
            active: {
                opacity: '0.5',
            },
        },
    }),
};

export const animateLayoutChanges: AnimateLayoutChanges = (args) => defaultAnimateLayoutChanges({ ...args, wasDragging: true });
export type DropId = TabName | keyof PlayState;

export const CardMenuContext = createContext<{
    state: PlayState,
    setState: React.Dispatch<React.SetStateAction<PlayState>>,
    setHasMulliganed: React.Dispatch<React.SetStateAction<boolean>>,
    selectedTabIndex: keyof typeof TAB_INDEX_NAME_MAP,
    setSelectedTab: React.Dispatch<React.SetStateAction<keyof typeof TAB_INDEX_NAME_MAP>>,
    onRevealX: () => void,
    setIsDeckHidden: (hidden: boolean) => void,
    isDeckHidden: boolean,
    onCardDoubleClick: (id: UniqueIdentifier, sourceId: keyof PlayState) => void
}>({
    setState: () => { },
    state: {
        breakZone: [],
        damageZone: [],
        deck: [],
        field1: [],
        field2: [],
        field3: [],
        hand: [],
        limitBreak: [],
        removedFromGame: [],
        revealed: []
    },
    setHasMulliganed: () => { },
    selectedTabIndex: 0,
    setSelectedTab: () => { },
    onRevealX: () => {},
    setIsDeckHidden: () => {},
    isDeckHidden: true,
    onCardDoubleClick: () => {}
});

export type MenuType = keyof PlayState;
export type MenuItemKey = keyof PlayState | "dull" | "activate" | "freeze" | "bottomOfDeck" | "topOfDeck" | "addCounter" | "removeCounter";

export const optionsByMenuType: Record<MenuType, Record<MenuItemKey, boolean>> = {
    breakZone: {
        breakZone: false,
        damageZone: false,
        deck: false,
        field1: true,
        field2: true,
        field3: true,
        hand: true,
        limitBreak: false,
        removedFromGame: true,
        dull: false,
        activate: false,
        freeze: false,
        revealed: false,
        bottomOfDeck: false,
        topOfDeck: false,
        addCounter: false,
        removeCounter: false
    },
    damageZone: {
        breakZone: false,
        damageZone: false,
        deck: false,
        field1: false,
        field2: false,
        field3: false,
        hand: false,
        limitBreak: false,
        removedFromGame: false,
        dull: false,
        activate: false,
        freeze: false,
        revealed: false,
        bottomOfDeck: false,
        topOfDeck: false,
        addCounter: false,
        removeCounter: false
    },
    deck: {
        breakZone: true,
        damageZone: true,
        deck: false,
        field1: true,
        field2: true,
        field3: true,
        hand: true,
        limitBreak: false,
        removedFromGame: false,
        dull: false,
        activate: false,
        freeze: false,
        revealed: true,
        bottomOfDeck: false,
        topOfDeck: false,
        addCounter: false,
        removeCounter: false
    },
    field1: {
        breakZone: true,
        damageZone: false,
        deck: false,
        field1: false,
        field2: false,
        field3: false,
        hand: true,
        limitBreak: false,
        removedFromGame: true,
        dull: true,
        activate: true,
        freeze: true,
        revealed: false,
        bottomOfDeck: true,
        topOfDeck: true,
        addCounter: true,
        removeCounter: true
    },
    field2: {
        breakZone: true,
        damageZone: false,
        deck: false,
        field1: false,
        field2: false,
        field3: false,
        hand: true,
        limitBreak: false,
        removedFromGame: true,
        dull: true,
        activate: true,
        freeze: true,
        revealed: false,
        bottomOfDeck: true,
        topOfDeck: true,
        addCounter: true,
        removeCounter: true
    },
    field3: {
        breakZone: true,
        damageZone: false,
        deck: false,
        field1: false,
        field2: false,
        field3: false,
        hand: true,
        limitBreak: false,
        removedFromGame: true,
        dull: true,
        activate: true,
        freeze: true,
        revealed: false,
        bottomOfDeck: true,
        topOfDeck: true,
        addCounter: true,
        removeCounter: true
    },
    hand: {
        breakZone: true,
        damageZone: false,
        deck: false,
        field1: true,
        field2: true,
        field3: true,
        hand: false,
        limitBreak: false,
        removedFromGame: true,
        dull: false,
        activate: false,
        freeze: false,
        revealed: false,
        bottomOfDeck: true,
        topOfDeck: true,
        addCounter: false,
        removeCounter: false
    },
    limitBreak: {
        breakZone: false,
        damageZone: false,
        deck: false,
        field1: true,
        field2: true,
        field3: true,
        hand: false,
        limitBreak: false,
        removedFromGame: true,
        dull: false,
        activate: false,
        freeze: false,
        revealed: false,
        bottomOfDeck: false,
        topOfDeck: false,
        addCounter: false,
        removeCounter: false
    },
    removedFromGame: {
        breakZone: false,
        damageZone: false,
        deck: false,
        field1: false,
        field2: false,
        field3: false,
        hand: false,
        limitBreak: false,
        removedFromGame: false,
        dull: false,
        activate: false,
        freeze: false,
        revealed: false,
        bottomOfDeck: false,
        topOfDeck: false,
        addCounter: true,
        removeCounter: true
    },
    revealed: {
        breakZone: true,
        damageZone: false,
        deck: false,
        field1: true,
        field2: true,
        field3: true,
        hand: true,
        limitBreak: false,
        removedFromGame: false,
        dull: false,
        activate: false,
        freeze: false,
        revealed: false,
        bottomOfDeck: true,
        topOfDeck: true,
        addCounter: false,
        removeCounter: false
    }
}

export const tabIndexes = {
    revealed: 6
}

export type TabIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type TabName = "hand-tab" | "deck-tab" | "limitBreak-tab" | "breakZone-tab" | "removedFromGame-tab" | "damageZone-tab" | "revealed-tab";

export const TAB_NAME_ZONE_NAME_MAP: Record<TabName, keyof PlayState> = {
    "breakZone-tab": "breakZone",
    "damageZone-tab": "damageZone",
    "deck-tab": "deck",
    "hand-tab": "hand",
    "limitBreak-tab": "limitBreak",
    "removedFromGame-tab": "removedFromGame",
    "revealed-tab": "revealed"
}

export const TAB_NAME_INDEX_MAP: Record<TabName, TabIndex> = {
    "hand-tab": 0,
    "deck-tab": 1,
    "limitBreak-tab": 2,
    "breakZone-tab": 3,
    "removedFromGame-tab": 4,
    "damageZone-tab": 5,
    "revealed-tab": 6
}

export const TAB_INDEX_NAME_MAP: Record<TabIndex, TabName> = {
    0: "hand-tab",
    1: "deck-tab",
    2: "limitBreak-tab",
    3: "breakZone-tab",
    4: "removedFromGame-tab",
    5: "damageZone-tab",
    6: "revealed-tab"
}

export type RevealXState = {
    isOpen: boolean,
    quantity: number | null,
    sourceTab: TabName
}

export function getDefaultRevealXState(): RevealXState {
    return {
        isOpen: false,
        quantity: 0,
        sourceTab: "deck-tab"
    }
}